/**
 * The grouping dimensions available when generating invoice lines.
 *
 * These live in their own leaf module, with no imports, because consumers that only need the
 * vocabulary should not have to load the line generators. invoicing_generate_lines.js reaches
 * invoicing_generate_lines_config.js -> util.js -> the canvas-scheduling tree -> the design
 * system, which cannot be loaded in a jsdom unit test; importing this file instead costs
 * nothing. invoicing_generate_lines.js re-exports both objects, so existing import sites keep
 * working unchanged.
 */

export const TIME_REG_GROUPINGS = {
	NO_GROUPING: 'NO_GROUPING',
	PROJECT: 'PROJECT',
	TASK: 'TASK',
	DONE_TASK: 'DONE_TASK',
	PERSON: 'PERSON',
	ROLE: 'ROLE',
	PHASE: 'PHASE',
	SPRINTS: 'SPRINTS',
	NO_GROUPING_NOTES: 'NO_GROUPING_NOTES',
};

export const EXPENSE_GROUPINGS = {
	NO_GROUPING: 'NO_GROUPING',
	PROJECT: 'PROJECT',
	PERSON: 'PERSON',
	PHASE: 'PHASE',
};

/**
 * Translation key for a time-registration grouping's display label.
 *
 * Lives here so callers do not each carry their own switch. Note that the Step 1 dropdown in
 * invoice_settings_and_line_generation.js still has its own inline mapping of the same keys; the two
 * are the same set and should be collapsed onto this helper when that file is next touched.
 */
export const timeRegGroupingLabelKey = (grouping: string): string => {
	switch (grouping) {
		case TIME_REG_GROUPINGS.NO_GROUPING:
			return 'project_sprints.group_by_people_disable';
		case TIME_REG_GROUPINGS.PROJECT:
			return 'common.project';
		case TIME_REG_GROUPINGS.TASK:
			return 'common.task';
		case TIME_REG_GROUPINGS.DONE_TASK:
			return 'invoice.done_task';
		case TIME_REG_GROUPINGS.PERSON:
			return 'common.person';
		case TIME_REG_GROUPINGS.ROLE:
			return 'common.role';
		case TIME_REG_GROUPINGS.PHASE:
			return 'common.phase';
		case TIME_REG_GROUPINGS.SPRINTS:
			return 'common.sprints';
		case TIME_REG_GROUPINGS.NO_GROUPING_NOTES:
			return 'invoice.no_grouping_notes';
		default:
			return 'common.person';
	}
};

/**
 * Translation key for the work-detail row that holds time the chosen dimension does not reach —
 * time logged with no task cannot be grouped by phase or sprint, and role and person are both
 * optional on a registration.
 *
 * Such a row is persisted with a null `groupLabel`, so the wording is resolved here at render time
 * rather than stored. That is what lets one snapshot read correctly as "No phase" or "No sprint"
 * depending on the dimension it is being displayed under, and in the reader's own language.
 *
 * Every key returned already exists and is already translated; none are specific to invoicing, which
 * is why they are borrowed from the time-registration report's vocabulary rather than duplicated.
 * The fallback covers the two ungrouped dimensions, whose label is built from role and person, so a
 * bucketed row there is time attributable to neither.
 */
export const workDetailUngroupedLabelKey = (grouping: string): string => {
	switch (grouping) {
		case TIME_REG_GROUPINGS.PHASE:
			return 'invoicing.work_detail.no_phase';
		case TIME_REG_GROUPINGS.SPRINTS:
			return 'invoicing.work_detail.no_sprint';
		case TIME_REG_GROUPINGS.ROLE:
			return 'time_reg_report.no_role';
		case TIME_REG_GROUPINGS.TASK:
		case TIME_REG_GROUPINGS.DONE_TASK:
			return 'time_reg_report.no_task';
		case TIME_REG_GROUPINGS.PROJECT:
			return 'time_reg_report.no_project';
		default:
			return 'invoicing.no_person';
	}
};
